import { NAVER_MAP_CLIENT_KEY } from "../config/keys";

const NAVER_MAP_SCRIPT_ID = NAVER_MAP_CLIENT_KEY;
const NAVER_MAP_LOAD_TIMEOUT_MS = 10000;

declare global {
  interface Window {
    naver?: any;
    __naverMapLoadingPromise?: Promise<any>;
  }
}

type LoadNaverMapOptions = {
  clientId: string;
  submodules?: string[];
};

export function loadNaverMap({
  clientId,
  submodules = [],
}: LoadNaverMapOptions): Promise<any> {
  if (!clientId) {
    return Promise.reject(new Error('NAVER_MAP_CLIENT_ID가 없습니다.'));
  }

  if (typeof window === 'undefined') {
    return Promise.reject(new Error('브라우저 환경에서만 사용할 수 있습니다.'));
  }

  if (window.naver?.maps) {
    return Promise.resolve(window.naver);
  }

  if (window.__naverMapLoadingPromise) {
    return window.__naverMapLoadingPromise;
  }

  window.__naverMapLoadingPromise = new Promise((resolve, reject) => {
    let timeoutId: number | null = null;

    const clearTimeoutIfNeeded = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const clearPendingPromise = () => {
      window.__naverMapLoadingPromise = undefined;
    };

    const removeScript = () => {
      document.getElementById(NAVER_MAP_SCRIPT_ID)?.remove();
    };

    const resolveNaver = () => {
      clearTimeoutIfNeeded();

      if (window.naver?.maps) {
        resolve(window.naver);
        return;
      }

      clearPendingPromise();
      removeScript();
      reject(new Error('네이버 지도 객체를 찾을 수 없습니다.'));
    };

    const rejectLoad = (message: string) => {
      clearTimeoutIfNeeded();
      clearPendingPromise();
      removeScript();
      reject(new Error(message));
    };

    timeoutId = window.setTimeout(() => {
      rejectLoad('네이버 지도 스크립트 로드 시간이 초과되었습니다.');
    }, NAVER_MAP_LOAD_TIMEOUT_MS);

    const existing = document.getElementById(NAVER_MAP_SCRIPT_ID) as HTMLScriptElement | null;

    if (existing) {
      existing.addEventListener('load', resolveNaver, { once: true });
      existing.addEventListener(
        'error',
        () => rejectLoad('네이버 지도 스크립트 로드에 실패했습니다.'),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.id = NAVER_MAP_SCRIPT_ID;

    const params = new URLSearchParams({
      ncpKeyId: clientId,
    });

    if (submodules.length > 0) {
      params.set('submodules', submodules.join(','));
    }

    script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?${params.toString()}`;
    script.async = true;

    script.onload = resolveNaver;
    script.onerror = () => {
      rejectLoad('네이버 지도 스크립트 로드에 실패했습니다.');
    };

    document.head.appendChild(script);
  });

  return window.__naverMapLoadingPromise;
}
